/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Link from 'flarum/common/components/Link';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexPageHero from 'flarum/newvigation/components/IndexPageHero';
import * as follow_tags from '@fof-follow-tags';
import type ItemList from 'flarum/common/utils/ItemList';
import type Mithril from 'mithril';
import type Tag from 'flarum/tags/common/models/Tag';

/**
 * A `flarum/tags` tag, extended with the subscription accessors that
 * `fof/follow-tags` adds to the model.
 */
export type FollowableTag = Tag & {
	canStartDiscussion(): boolean;
	subscription(): string | null;
};

/**
 * Attrs of `glowingblue/newvigation`'s `IndexPageHero`.
 */
export interface IndexPageHeroAttrs {
	currentTag?: FollowableTag;
	type?: string;
}

/**
 * The slice of `glowingblue/newvigation`'s `IndexPageHero` used here.
 */
export type IndexPageHeroLike = {
	attrs: IndexPageHeroAttrs;
	items(): ItemList<Mithril.Children>;
	oncreate(vnode: Mithril.VnodeDOM): void;
	$(selector?: string): JQuery;
};

/**
 * Attrs of the "New Discussion" button, whose inline colouring is stripped below.
 */
export interface NewDiscussionButtonAttrs {
	style?: string;
}

export default function () {
	extend(IndexPage.prototype, 'sidebarItems', function (this: IndexPage, items: ItemList<Mithril.Children>) {
		const title = app.translator.trans('glowingblue-orell-thalia-common.forum.index.all_tags');
		items.add('title', <h3>{this.currentTag() ? <Link href={app.route('index', app.search.stickyParams())}>{title}</Link> : title}</h3>, 150);

		if (app.initializers.has('fof/follow-tags')) {
			if (items.has('subscriptionButton')) {
				items.remove('subscriptionButton');
			}
		}
	});

	if (app.initializers.has('fof/follow-tags') && app.initializers.has('glowingblue-newvigation')) {
		const SubscriptionStateButton = follow_tags.components.SubscriptionStateButton;
		const SubscriptionModal = follow_tags.components.SubscriptionModal;

		extend(IndexPageHero.prototype as IndexPageHeroLike, 'items', function (this: IndexPageHeroLike, items: ItemList<Mithril.Children>) {
			const { currentTag, type } = this.attrs;
			if (type === 'discussions') {
				const sidebarItems = IndexPage.prototype.sidebarItems();
				let newDiscussionBtn: Mithril.Vnode<NewDiscussionButtonAttrs> | null = sidebarItems.get(
					'newDiscussion'
				) as Mithril.Vnode<NewDiscussionButtonAttrs>;
				newDiscussionBtn.attrs.style = undefined; // Remove the tag specific coloring

				// !!! Hard-coded !!!
				// In the `leserunden` tag,
				// We want to hide the new discussion button if the user is not allowed to create discussions
				if (currentTag && currentTag.slug() === 'leserunden') {
					const canStartDiscussion = currentTag.canStartDiscussion();
					if (!canStartDiscussion) {
						newDiscussionBtn = null;
					}
				}

				items.add(
					'controls',
					<div className="IndexPageHero-controls">
						{newDiscussionBtn}
						{currentTag && app.session.user && (
							<SubscriptionStateButton
								className="Button App-primaryControl SubscriptionStateButton"
								subscription={currentTag.subscription()}
								onclick={() => app.modal.show(SubscriptionModal, { model: currentTag })}
							/>
						)}
					</div>
				);
			}
		});
	}

	if (app.initializers.has('glowingblue-newvigation')) {
		extend(IndexPageHero.prototype as IndexPageHeroLike, 'oncreate', function (this: IndexPageHeroLike) {
			if ((this.$().height() ?? 0) >= 170) {
				this.$().addClass('is-high-hero');
			}
		});
	}
}
